import { useState } from "react";
import { Plus, Car } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { VehicleFormDialog, VehicleFormValues } from "@/components/vehicles/VehicleFormDialog";
import { VehicleListItem } from "@/components/vehicles/VehicleListItem";
import { MergeVehicleDialog } from "@/components/vehicles/MergeVehicleDialog";
import { DeleteVehicleDialog } from "@/components/vehicles/DeleteVehicleDialog";
import { useVehicles, Vehicle } from "@/hooks/useVehicles";

const Vehicles = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [mergingVehicle, setMergingVehicle] = useState<Vehicle | null>(null);
  const [deletingVehicle, setDeletingVehicle] = useState<Vehicle | null>(null);
  const { vehicles, isLoading, addVehicle, updateVehicle, deleteVehicle, mergeVehicles } = useVehicles();

  const handleAddVehicle = (values: VehicleFormValues) => {
    addVehicle.mutate({
      name: values.name,
      year: values.year,
      make: values.make,
      model: values.model,
      trim: values.trim,
      trim_variation: values.trim_variation,
      license_plate: values.license_plate,
      vin: values.vin,
      turo_vehicle_id: values.turo_vehicle_id,
      color: values.color,
      mileage: values.mileage,
      image_url: values.image_url,
    }, {
      onSuccess: () => setIsDialogOpen(false),
    });
  };

  const handleEditVehicle = (values: VehicleFormValues) => {
    if (!editingVehicle) return;
    updateVehicle.mutate(
      {
        id: editingVehicle.id,
        name: values.name,
        year: values.year,
        make: values.make,
        model: values.model,
        trim: values.trim,
        trim_variation: values.trim_variation,
        license_plate: values.license_plate,
        vin: values.vin,
        turo_vehicle_id: values.turo_vehicle_id,
        color: values.color,
        mileage: values.mileage,
        image_url: values.image_url,
      },
      { onSuccess: () => setEditingVehicle(null) }
    );
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
  };

  const handleMerge = (vehicle: Vehicle) => {
    setMergingVehicle(vehicle);
  };

  const handleDelete = (vehicle: Vehicle) => {
    setDeletingVehicle(vehicle);
  };

  const handleMergeConfirm = (sourceId: string, destinationId: string) => {
    mergeVehicles.mutate(
      { sourceId, destinationId },
      { onSuccess: () => setMergingVehicle(null) }
    );
  };

  const handleDeleteConfirm = (vehicleId: string) => {
    deleteVehicle.mutate(vehicleId, {
      onSuccess: () => setDeletingVehicle(null),
    });
  };

  const handleAddClick = () => {
    setIsDialogOpen(true);
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Vehicles</h1>
          </div>
          <Button onClick={handleAddClick}>
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </Button>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading vehicles...</div>
        ) : vehicles && vehicles.length > 0 ? (
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {vehicles.map((vehicle) => (
              <VehicleListItem
                key={vehicle.id}
                vehicle={vehicle}
                onEdit={handleEdit}
                onMerge={handleMerge}
                onDelete={handleDelete}
                showMerge={vehicles.length > 1}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 rounded-full bg-muted mb-4">
              <Car className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-1">No vehicles yet</h3>
            <p className="text-muted-foreground mb-4">
              Add your first vehicle to start tracking trips and expenses.
            </p>
            <Button onClick={handleAddClick}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Vehicle
            </Button>
          </div>
        )}

        <VehicleFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSubmit={handleAddVehicle}
          isLoading={addVehicle.isPending}
        />

        <VehicleFormDialog
          open={!!editingVehicle}
          onOpenChange={(open) => !open && setEditingVehicle(null)}
          onSubmit={handleEditVehicle}
          defaultValues={editingVehicle ?? undefined}
          isEditing
          isLoading={updateVehicle.isPending}
          vehicleId={editingVehicle?.id}
        />

        {mergingVehicle && vehicles && (
          <MergeVehicleDialog
            open={!!mergingVehicle}
            onOpenChange={(open) => !open && setMergingVehicle(null)}
            sourceVehicle={mergingVehicle}
            vehicles={vehicles}
            onMerge={handleMergeConfirm}
            isLoading={mergeVehicles.isPending}
          />
        )}

        {deletingVehicle && (
          <DeleteVehicleDialog
            open={!!deletingVehicle}
            onOpenChange={(open) => !open && setDeletingVehicle(null)}
            vehicle={deletingVehicle}
            onDelete={handleDeleteConfirm}
            isLoading={deleteVehicle.isPending}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default Vehicles;
